/**
 * The composer's voice note: the microphone, a level read every tick for the live bars, the clock,
 * and the recording handed back when the trainer stops.
 *
 * Phases: `idle` → `recording` (bars move, the clock runs) → `review` (still bars, the length, send
 * or discard). Cancelling from `recording` throws the take away. The recording stops itself at the
 * slot's five minutes.
 *
 * The microphone is released the moment recording stops, whichever way — a browser shows its
 * recording indicator for as long as a track is open, and a trainer who sees it after pressing stop
 * has been told something false.
 *
 * `MediaRecorder` writes whatever this browser writes (WebM in Chrome and Firefox, MP4 in Safari);
 * `shared/media/audio/encodeVoiceNote` makes the M4A the slot accepts out of it at send time.
 */

import { useEffect, useRef, useState } from 'react';

import { VOICE_CLASS } from '~/shared/media/audio/encodeVoiceNote';

import { levelOfRms } from '../model/voicePeaks';

const TICK_MS = 90;

export type VoiceRecorderState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'recording'; readonly seconds: number; readonly levels: readonly number[] }
  | {
      readonly phase: 'review';
      readonly seconds: number;
      readonly levels: readonly number[];
      readonly recording: Blob;
    };

export type VoiceRecorderRefusal = 'denied' | 'missing' | 'busy' | 'unavailable';

export interface VoiceRecorder {
  readonly state: VoiceRecorderState;
  readonly refusal: VoiceRecorderRefusal | null;
  readonly start: () => void;
  readonly stop: () => void;
  readonly cancel: () => void;
  readonly discard: () => void;
  readonly dismissRefusal: () => void;
}

function refusalOf(error: unknown): VoiceRecorderRefusal {
  if (!(error instanceof DOMException)) return 'unavailable';
  switch (error.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'missing';
    case 'NotReadableError':
    case 'AbortError':
      return 'busy';
    default:
      return 'unavailable';
  }
}

interface Session {
  stream: MediaStream;
  recorder: MediaRecorder;
  context: AudioContext;
  timer: ReturnType<typeof setInterval>;
  chunks: Blob[];
  levels: number[];
  startedAt: number;
  keep: boolean;
}

function release(session: Session): void {
  clearInterval(session.timer);
  for (const track of session.stream.getTracks()) track.stop();
  void session.context.close().catch(() => undefined);
}

export function useVoiceRecorder(): VoiceRecorder {
  const [state, setState] = useState<VoiceRecorderState>({ phase: 'idle' });
  const [refusal, setRefusal] = useState<VoiceRecorderRefusal | null>(null);
  const session = useRef<Session | null>(null);

  useEffect(
    () => () => {
      const live = session.current;
      if (live === null) return;
      live.keep = false;
      if (live.recorder.state !== 'inactive') live.recorder.stop();
      release(live);
      session.current = null;
    },
    [],
  );

  const finish = (keep: boolean) => {
    const live = session.current;
    if (live === null) return;
    live.keep = keep;
    if (live.recorder.state !== 'inactive') live.recorder.stop();
    else release(live);
  };

  const start = () => {
    if (session.current !== null) return;
    setRefusal(null);
    if (typeof MediaRecorder === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
      setRefusal('unavailable');
      return;
    }
    void (async () => {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        setRefusal(refusalOf(error));
        return;
      }
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        for (const track of stream.getTracks()) track.stop();
        setRefusal('unavailable');
        return;
      }
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);

      const live: Session = {
        stream,
        recorder,
        context,
        timer: setInterval(() => {
          analyser.getFloatTimeDomainData(samples);
          let sum = 0;
          for (const value of samples) sum += value * value;
          live.levels.push(levelOfRms(Math.sqrt(sum / samples.length)));
          const seconds = (performance.now() - live.startedAt) / 1_000;
          setState({ phase: 'recording', seconds, levels: live.levels.slice(-120) });
          if (seconds >= VOICE_CLASS.maxDurationSeconds) finish(true);
        }, TICK_MS),
        chunks: [],
        levels: [],
        startedAt: performance.now(),
        keep: true,
      };
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) live.chunks.push(event.data);
      });
      recorder.addEventListener('stop', () => {
        release(live);
        session.current = null;
        const seconds = (performance.now() - live.startedAt) / 1_000;
        if (!live.keep || live.chunks.length === 0 || seconds < 0.5) {
          setState({ phase: 'idle' });
          return;
        }
        setState({
          phase: 'review',
          seconds,
          levels: live.levels,
          recording: new Blob(live.chunks, { type: recorder.mimeType || 'audio/webm' }),
        });
      });
      session.current = live;
      recorder.start(250);
      setState({ phase: 'recording', seconds: 0, levels: [] });
    })();
  };

  return {
    state,
    refusal,
    start,
    stop: () => {
      finish(true);
    },
    cancel: () => {
      finish(false);
    },
    discard: () => {
      setState({ phase: 'idle' });
    },
    dismissRefusal: () => {
      setRefusal(null);
    },
  };
}
